import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { t, useI18n } from "../../i18n";
import "./TemplatesShowcasePage.css";
import "./LandingPageV2.css";

const API_URL = process.env.REACT_APP_API_URL || "https://b.supchats.com";

export default function TemplatesShowcasePage() {
  const navigate = useNavigate();
  const { locale, setLocale } = useI18n();
  const [modalTemplate, setModalTemplate] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [templatesError, setTemplatesError] = useState(null);

  const fetchTemplates = () => {
    setTemplatesError(null);
    setTemplatesLoading(true);
    axios
      .get(`${API_URL}/api/templates`, { withCredentials: false })
      .then((res) => {
        if (res.data?.ok && Array.isArray(res.data.templates)) {
          setTemplates(res.data.templates);
        } else {
          setTemplatesError(t("Failed to load templates"));
        }
      })
      .catch(() => {
        setTemplatesError(t("Failed to load templates. Please try again."));
      })
      .finally(() => {
        setTemplatesLoading(false);
      });
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleCardClick = (tpl) => {
    if (tpl.id === "custom") {
      setModalTemplate(tpl);
      return;
    }
    navigate(`/templates/${tpl.id}`);
  };

  const handleTryTemplate = () => {
    if (!modalTemplate) return;
    try {
      localStorage.setItem("auth_mode", "signup");
      localStorage.setItem("template_interest", modalTemplate.id);
    } catch (e) {}
    setModalTemplate(null);
    navigate(`/login?mode=signup&template=${modalTemplate.id}`);
  };

  const handleLocaleToggle = () => {
    setLocale(locale === "ru" ? "en" : "ru");
  };

  const handleSignUp = () => {
    try {
      localStorage.setItem("auth_mode", "signup");
    } catch (e) {}
    navigate("/login?mode=signup");
  };

  const handleSignIn = () => {
    try {
      localStorage.setItem("auth_mode", "login");
    } catch (e) {}
    navigate("/login");
  };

  return (
    <div className="templates-page">
      <header className="landing-v2-header">
        <div className="header-container">
          <Link to="/" className="logo">
            <img
              src={process.env.PUBLIC_URL + "/logo512.png"}
              alt="Supchats logo"
              className="logo-img"
            />
            <span className="logo-text">Supchats</span>
          </Link>
          <nav className="nav-menu">
            <div className="language-switcher">
              <label className="language-toggle-label">
                <input
                  type="checkbox"
                  checked={locale === "ru"}
                  onChange={handleLocaleToggle}
                  className="language-toggle-checkbox"
                />
                <span className="language-toggle-slider">
                  <span className="language-toggle-text">{locale === "ru" ? "RU" : "EN"}</span>
                </span>
              </label>
            </div>
            <button type="button" className="btn-signup" onClick={handleSignUp}>
              {t("Sign Up")}
            </button>
            <button type="button" className="btn-signin" onClick={handleSignIn}>
              {t("Sign In")}
            </button>
          </nav>
        </div>
      </header>

      <main className="templates-main">
        <section className="templates-hero">
          <h1 className="templates-hero-title">{t("Choose a template for your business, and we'll help you set up your agent after registration.")}</h1>
          <p className="templates-hero-subtitle">{t("7 ready-made scenarios for common business tasks. Launch in minutes, no code required.")}</p>
        </section>

        <section className="templates-grid">
          {templatesLoading ? (
            <p className="templates-loading">{t("Loading...")}</p>
          ) : templatesError ? (
            <div className="templates-error">
              <p className="templates-error-message">{templatesError}</p>
              <button type="button" className="templates-retry-btn" onClick={fetchTemplates}>
                {t("Retry")}
              </button>
            </div>
          ) : (
            templates.map((tpl) => (
              <button
                key={tpl.id}
                type="button"
                className="templates-card"
                onClick={() => handleCardClick(tpl)}
              >
                <div className="templates-card-icon">{tpl.icon}</div>
                <h3 className="templates-card-title">{t(tpl.title)}</h3>
                <p className="templates-card-desc">{t(tpl.desc)}</p>
                <span className="templates-card-cta">{t("More details")}</span>
              </button>
            ))
          )}
        </section>

        {!templatesLoading && !templatesError && templates.length > 0 && (
          <p className="templates-register-simple">
            {t("Not sure which template fits?")}{" "}
            <button
              type="button"
              className="templates-register-simple-link"
              onClick={handleSignUp}
            >
              {t("Register without template")}
            </button>
          </p>
        )}
      </main>

      {modalTemplate && (
        <div className="templates-modal-backdrop" onClick={() => setModalTemplate(null)}>
          <div className="templates-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="templates-modal-close"
              onClick={() => setModalTemplate(null)}
              aria-label={t("Close")}
            >
              ×
            </button>
            <div className="templates-modal-icon">{modalTemplate.icon}</div>
            <h2 className="templates-modal-title">{t(modalTemplate.title)}</h2>
            <p className="templates-modal-desc">{t(modalTemplate.detail)}</p>
            <p className="templates-modal-next">
              {t("After registration, we'll help you set up the agent for this scenario. One-click widget installation.")}
            </p>
            <button
              type="button"
              className="templates-modal-try"
              onClick={handleTryTemplate}
            >
              {t("Try this template")}
            </button>
          </div>
        </div>
      )}

      <footer className="templates-footer">
        <Link to="/" className="templates-footer-link">
          {t("Back to main page")}
        </Link>
      </footer>
    </div>
  );
}
